import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Toast from "react-bootstrap/Toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faImage, faFileExcel, faFile } from "@fortawesome/free-solid-svg-icons";
import { deleteFile } from "../services/api";

const Dropzone = ({ fieldName, onFileUpload, fileTypes = [], showPreview = false, existingFileIds = [], maxFileSizeMB = 250 }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const MAX_FILE_SIZE = maxFileSizeMB * 1024 * 1024;

  // Initialize with existing file IDs when editing
  useEffect(() => {
    if (existingFileIds && existingFileIds.length > 0) {
      const normalizedIds = Array.isArray(existingFileIds) 
        ? existingFileIds.filter(id => id != null && id !== '')
        : [existingFileIds].filter(id => id != null && id !== '');
      
      if (normalizedIds.length > 0) {
        setUploadedFileIds(normalizedIds);
        // Notify parent of initial files
        onFileUpload(fieldName, normalizedIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to initialize with existing files

  const onDrop = async (acceptedFiles) => {
    setIsUploading(true);
    setToastMessage("");

    const filteredFiles = acceptedFiles.filter(file => {
      const isValidType = fileTypes.some(type => {
        if (type.endsWith("/*")) {
          const baseType = type.split("/")[0];
          return file.type.startsWith(baseType + "/");
        }
        return file.type === type;
      });
    
      const isValidSize = file.size <= MAX_FILE_SIZE;
      return isValidType && isValidSize;
    });

    if (filteredFiles.length === 0) {
      setToastMessage(`file were too large or had invalid types (Max ${maxFileSizeMB} MB).`);
      setShowToast(true);
      setIsUploading(false);
      return;
    }

    const existingFileNames = files.map(file => file.name);
    const uniqueFiles = filteredFiles.filter(file => !existingFileNames.includes(file.name));

    if (uniqueFiles.length === 0) {
      setToastMessage("Duplicate file detected! This file is already uploaded.");
      setShowToast(true);
      setIsUploading(false);
      return;
    }

    try {

      const uploadedIds = await uploadFilesToServer(uniqueFiles);
      setFiles((prev) => [...prev, ...uniqueFiles]);
      setUploadedFileIds((prev) => [...prev, ...uploadedIds]);
      onFileUpload(fieldName, [...uploadedFileIds, ...uploadedIds]);
      setToastMessage("File(s) uploaded successfully!");
      setShowToast(true);
    } catch (error) {
      console.error("File upload failed:", error);
      setToastMessage("Failed to upload files. Please try again.");
      setShowToast(true);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFilesToServer = (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return fetch(`${process.env.REACT_APP_API_URL}/attachments/upload-files`, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          const documentIds = data.documentIds || [];
          return Array.isArray(documentIds) ? documentIds : [documentIds];
        } else {
          throw new Error(data.message || "File upload failed");
        }
      })
      .catch((error) => {
        console.error("Error during file upload:", error);
        throw error;
      });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: fileTypes.length > 0 ? fileTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}) : undefined,
    multiple: true, // Allow multiple files
  });

  const handleRemoveFile = async (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    
    const fileIdToDelete = uploadedFileIds[index];
    
    // If there's a file ID, delete it from the server
    if (fileIdToDelete) {
      try {
        const data = await deleteFile(fileIdToDelete);
        
        if (!data.success) {
          setToastMessage("Failed to delete file from server. Please try again.");
          setShowToast(true);
          return;
        }
      } catch (error) {
        console.error("Error deleting file:", error);
        setToastMessage("Failed to delete file from server. Please try again.");
        setShowToast(true);
        return;
      }
    }
    
    // Remove from local state
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
  
    const updatedFileIds = [...uploadedFileIds];
    updatedFileIds.splice(index, 1);
  
    setFiles(updatedFiles);
    setUploadedFileIds(updatedFileIds);
  
    onFileUpload(fieldName, updatedFileIds);
    setToastMessage("File deleted successfully!");
    setShowToast(true);
  };

  // Function to render the file icon based on file type
  const getFileIcon = (file) => {
    if (file.type?.startsWith("image/")) {
      return <FontAwesomeIcon icon={faImage} size="2x" style={{ color: "blue" }} />;
    } else if (file.type === "application/pdf") {
      return <FontAwesomeIcon icon={faFilePdf} size="2x" style={{ color: "red" }} />;
    } else if (
      file.type === "application/vnd.ms-excel" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return <FontAwesomeIcon icon={faFileExcel} size="2x" style={{ color: "green" }} />;
    } else {
      return <FontAwesomeIcon icon={faFile} size="2x" style={{ color: "black" }} />;
    }
  };

  return (
    <div className="px-0">
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 1050,
        }}
      >
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg={toastMessage.includes("success") ? "success" : "warning"}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastMessage.includes("success") ? "Success" : "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body
            className={toastMessage.includes("success") ? "text-white" : ""}
          >
            {toastMessage}
          </Toast.Body>
        </Toast>
      </div>

      <Row className="no-gutters">
        <Col sm="12">
          <div className="dropzone-container mt-3">
            <div
              className="d-flex flex-column align-items-center dropzone-wrapper"
              style={{ color: isDragActive ? "#007bff" : "#333" }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <div className="loading-container">
                  <Spinner animation="border" />
                  <div className="mt-2">Uploading...</div>
                </div>
              ) : isDragActive ? (
                <div className="drpzone-internal">
                  <div className="my-2">
                    <img src="../backup.png" alt="Backup" />
                  </div>
                  <div className="mb-2 drag-text">Drop the files here ...</div>
                  <div className="max-length mb-2">Max {maxFileSizeMB} MB files are allowed</div>
                </div>
              ) : (
                <div className="drpzone-internal">
                  <div className="my-2">
                    <img src={`${process.env.PUBLIC_URL}/backup.svg`} alt="Backup" />
                  </div>
                  <div className="mb-2 drag-text">
                    Drag your file(s) or <span>browse</span>
                  </div>
                  <div className="max-length mb-2">Max {maxFileSizeMB} MB files are allowed</div>
                </div>
              )}
            </div>

            {/* Display Uploaded Files and Previews */}
            <div className="uploaded-files mt-3" onClick={(e) => e.stopPropagation()}>
              {files.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {files.map((file, index) => (
                     <li key={index} className="file-item d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid #ddd", padding: "10px 0" }}>
                     <div className="d-flex align-items-center">
                       <div className="file-icon me-2">{getFileIcon(file)}</div>
                       <span style={{ fontSize: "14px", color: "#333" }}>{file.name}</span>
                     </div>
                     <button 
                       type="button" 
                       className="btn btn-sm btn-danger ms-2" 
                       onClick={(e) => handleRemoveFile(e, index)}
                       style={{ zIndex: 10, position: 'relative' }}
                     >
                       Remove
                     </button>
                   </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Dropzone;